/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setIsStaticAddColumnDropdownMenuOpen, setActiveModal } from '../../ducks/calendarActions';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';

const AddPropertyHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  cursor: pointer;
  color: #444444;
  width: 100%;
`;

const AddPropertyInputBox = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 32px;
  border: 1px dashed #4A90E2;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  min-width: 160px;

  &:hover {
    background: rgba(74, 144, 226, 0.05);
  }
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 4px 4px;
  background-color: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
`;

const DropdownHeader = styled('div')`
  background-color: white;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  padding: 7px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

const AddPropContainer = styled('div')`
  font-family: 'Nunito Sans';
  color: #444444;
  margin-bottom: 0px;
  font-size: 16px;
`;

/** Wrapper around the dropdown trigger. When a custom `trigger` is
 * supplied (transposed pre-save / saved-edit footer button) it becomes
 * the keyboard-accessible control, so surface a light-blue highlight +
 * background on keyboard focus. */
const TriggerWrapper = styled('div')`
  width: 100%;
  border-radius: 4px;
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: #E8F0FE;
  }
`;

// Create a new HoverableMenuItem component similar to CalendarItemDropdownOptions
const HoverableMenuItem = ({ item, onClick, isFocused }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isHighlighted = isHovered || isFocused;

  return (
    <MenuItem
      style={{
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: 14,
        paddingRight: 7,
        cursor: 'pointer',
        background: isHighlighted ? '#F5F5F5' : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        {item.icon ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={18}
              icon={item.icon}
              color='#9E9E9E'
              style={{ marginRight: 5 }}
            />
            <MenuText>{item.label}</MenuText>
          </div>
        ) : (
          <ArrowDiv>
            <MenuText>{item.label}</MenuText>
          </ArrowDiv>
        )}
      </IconContainer>
    </MenuItem>
  );
};

HoverableMenuItem.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  isFocused: PropTypes.bool,
};

HoverableMenuItem.defaultProps = {
  isFocused: false,
};

const capWords = (str) => str
  .split(' ')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  .join(' ');

const AddPropertyColumnComponent = ({
  mode,
  primaryRows,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  setPrimaryRowSwitchState,
  assignedId,
  trigger,
  closeOnSelect,
  onPropertyAdded,
}) => {
  const dispatch = useDispatch();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const activeExerciseId = useSelector((state) => state.calendar.data.activeExerciseId);

  // Keyboard shortcut: Ctrl+P to open dropdown
  // Only responds when the parent exercise is the active exercise
  // Only enable for 'input' mode to prevent multiple dropdowns from opening
  useEffect(() => {
    // Only attach keyboard shortcut to 'input' mode instance
    if (mode !== 'input') {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        /** preventDefault stays ahead of every bail so the browser's native
         * Print dialog never appears while a calendar row is mounted. */
        e.preventDefault();
        /** Second route into the same double-navigation: opening this on top
         * of the options menu leaves both arrowing off one keypress. */
        if (hasArrowKeyOwner()) return;
        if (assignedId && assignedId !== activeExerciseId) return;
        setIsDropdownVisible(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeExerciseId, assignedId, mode]);

  // Filter to get only visible (not already selected) items
  const visibleItems = primaryRows.filter((row) => !primaryRowSwitch[row.name] && row.name !== 'add-property');

  // Arrow key navigation when dropdown is open
  useEffect(() => {
    if (!isDropdownVisible) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // +1 for "About Properties" item at the end
        setFocusedIndex((prev) => Math.min(prev + 1, visibleItems.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault();
        if (focusedIndex < visibleItems.length) {
          // Click on a property item
          const row = visibleItems[focusedIndex];
          const flippedStatus = !primaryRowSwitch[row.name];
          const newSwitch = { ...primaryRowSwitch };
          newSwitch[row.name] = flippedStatus;

          if (!flippedStatus) {
            setPrimaryRowSwitch(newSwitch);
          } else if (row.name === 'peak power' || row.name === 'velocity') {
            setPrimaryRowSwitch(newSwitch);
          } else {
            setPrimaryRowSwitchState(newSwitch);
          }
          if (flippedStatus && onPropertyAdded) onPropertyAdded(row.name);
        } else {
          // Click on "About Properties"
          dispatch(setActiveModal('aboutExercisePropertiesModal'));
        }
        setIsDropdownVisible(false);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsDropdownVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isDropdownVisible,
    focusedIndex,
    visibleItems,
    primaryRowSwitch,
    setPrimaryRowSwitch,
    setPrimaryRowSwitchState,
    dispatch,
    onPropertyAdded,
  ]);

  // Reset focused index when dropdown opens/closes
  useEffect(() => {
    if (isDropdownVisible) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isDropdownVisible]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!isDropdownVisible) return undefined;
    return claimArrowKeys();
  }, [isDropdownVisible]);

  const handleMenuItemClick = (row, e) => {
    e.domEvent.stopPropagation();
    const flippedStatus = !primaryRowSwitch[row.name];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[row.name] = flippedStatus;

    if (!flippedStatus) {
      setPrimaryRowSwitch(newSwitch);
    } else if (row.name === 'peak power' || row.name === 'velocity') {
      setPrimaryRowSwitch(newSwitch);
    } else {
      setPrimaryRowSwitchState(newSwitch);
    }
    if (flippedStatus && onPropertyAdded) onPropertyAdded(row.name);
    if (closeOnSelect) {
      setIsDropdownVisible(false);
    }
  };

  const handleAboutPropertiesClick = () => {
    dispatch(setActiveModal('aboutExercisePropertiesModal'));
  };

  const menu = (
    <Menu
      style={{
        width: 280,
        minHeight: 100,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
      }}
    >
      <AddPropContainer onClick={(e) => e.stopPropagation()}>
        <DropdownHeader>
          <Text fontWeight={700} fontSize='16px'>
            Add Property
          </Text>
          <Text fontSize='16px' color='#9E9E9E'>
            Ctrl+P
          </Text>
        </DropdownHeader>
      </AddPropContainer>
      <MenuWrapper style={{ gap: 4 }}>
        {primaryRows.map((primaryRow, index) => {
          if (!primaryRowSwitch[primaryRow.name] && primaryRow.name !== 'add-property') {
            const visibleIndex = visibleItems.indexOf(primaryRow);
            return (
              <HoverableMenuItem
                key={`first-${primaryRow.name}-${index}`}
                item={{
                  label: primaryRow.name === '%pr' ? '%PR' : capWords(primaryRow.name.includes('intensity') ? 'Intensity' : primaryRow.name.includes('rest per rep') ? 'Rest Per Rep' : primaryRow.name),
                }}
                onClick={(e) => handleMenuItemClick(primaryRow, e)}
                isFocused={focusedIndex === visibleIndex}
              />
            );
          }
          return null;
        })}
        <CustomDivider />
        <HoverableMenuItem
          key='about-properties'
          item={{
            label: 'About Properties',
            icon: 'info2',
          }}
          onClick={handleAboutPropertiesClick}
          isFocused={focusedIndex === visibleItems.length}
        />
      </MenuWrapper>
    </Menu>
  );

  const handleClick = (e) => {
    e.stopPropagation();
    setIsDropdownVisible(!isDropdownVisible);
  };

  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      visible={isDropdownVisible}
      onVisibleChange={(visible) => {
        setIsDropdownVisible(visible);
        dispatch(setIsStaticAddColumnDropdownMenuOpen(visible));
      }}
    >
      <TriggerWrapper
        style={trigger ? { display: 'inline-flex', width: 'auto' } : undefined}
        onClick={handleClick}
        /** When a custom trigger is supplied, make this wrapper the
         * keyboard-accessible control: tabbable, button semantics, and
         * Enter/Space (or ArrowDown) opens the dropdown. Once open, the
         * existing arrow/Enter handlers select a property. Legacy
         * header/input modes keep their original non-tabbable behavior. */
        {...(trigger ? {
          role: 'button',
          tabIndex: 0,
          'aria-haspopup': 'listbox',
          'aria-expanded': isDropdownVisible,
          'aria-label': 'Add property',
          onKeyDown: (e) => {
            /** An open popup owns the keyboard. The options menu is
             * highlight-only, so focus can still be sitting on this trigger
             * while the coach navigates it — without this bail, ArrowDown
             * opens this dropdown too and both menus then arrow in lockstep
             * off a single keypress. */
            if (hasArrowKeyOwner()) return;
            /** Enter / Space / ArrowDown all just open the dropdown (like
             * the superset picker). stopPropagation is required because
             * the open-dropdown's arrow/Enter navigation runs on a global
             * window keydown listener — without it, the same Enter that
             * opens the menu also bubbles up and immediately selects the
             * first property. */
            if (!isDropdownVisible && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setIsDropdownVisible(true);
            }
          },
        } : {})}
      >
        {trigger || (mode === 'header' ? (
          <AddPropertyHeader>
            <span>Add Property</span>
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='down-arrow'
              color='#444444'
            />
          </AddPropertyHeader>
        ) : (
          <AddPropertyInputBox>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='plus'
              color='#4A90E2'
            />
          </AddPropertyInputBox>
        ))}
      </TriggerWrapper>
    </Dropdown>
  );
};

AddPropertyColumnComponent.propTypes = {
  mode: PropTypes.oneOf(['header', 'input']).isRequired,
  primaryRows: PropTypes.instanceOf(Array).isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  setPrimaryRowSwitchState: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  assignedId: PropTypes.string,
  trigger: PropTypes.node,
  closeOnSelect: PropTypes.bool,
  onPropertyAdded: PropTypes.func,
};

AddPropertyColumnComponent.defaultProps = {
  containerRef: null,
  trigger: null,
  assignedId: null,
  closeOnSelect: false,
  onPropertyAdded: null,
};

export default AddPropertyColumnComponent;
